import {
  Suspense,
  useId,
  useMemo,
  useState,
  useTransition,
  type FC
} from 'react';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import { useLocalStorage } from '@/react/hooks/useLocalStorage';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { observer } from 'mobx-react-lite';
import {
  coerceDateToZone,
  convertTimestampToDateTimeLocalString
} from '@/utils/date-utils';
import { isNullOrEmpty } from '@/utils/null-utils';
import { PreviewService } from '@/services/isomorphic/PreviewService';
import LazyValue from '@/utils/LazyValue';
import { Button } from '../../core-ui/Button';
import { Icon, IconTypes } from '../../core-ui/Icon';
import { PastPreviewAlert } from './internal/PastPreviewAlert';
import { usePreview } from './usePreview';
import { CommonErrorBoundary } from '../../errors/boundaries/CommonErrorBoundary';

import S from './styles.module.scss';

/**
 * Converts a local datetime string in the Pacific timezone to a timestamp (in milliseconds since epoch).
 * @param datetimeString - A string in the format "YYYY-MM-DDTHH:MM" representing the datetime in
 * the Pacific timezone.
 * @returns The timestamp (in milliseconds since epoch).
 */
function convertPacificDateTimeLocalStringToTimestamp(
  datetimeString: string
): number {
  const localDate = new Date(datetimeString);
  const timezonedDate = coerceDateToZone(localDate, 'America/Los_Angeles');
  return timezonedDate.toMillis();
}

const pacificDatetimeFormatter = new LazyValue(() => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Los_Angeles'
  });
});

const previewDateFormatter = new LazyValue(() => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles'
  });
});

/**
 * A component that allows setting a preview date.
 */
const PreviewMenu: FC = observer(function PreviewMenu() {
  const [isStartPending, startStartTransition] = useTransition();
  const [isStopPending, startStopTransition] = useTransition();
  const [preview, setPreview] = usePreview();
  const inputId = useId();
  const startBtnId = useId();

  const inputDefaultValue = useMemo(() => {
    return preview !== null
      ? convertTimestampToDateTimeLocalString(
          preview,
          pacificDatetimeFormatter.value
        )
      : '';
  }, [preview]);

  const [inputValue, setInputValue] = useState(inputDefaultValue);

  const isInputDifferentFromCurrentPreview = useMemo(() => {
    const inputAsNonEmptyOrNull = inputValue === '' ? null : inputValue;
    if (inputAsNonEmptyOrNull === null || preview === null)
      return inputAsNonEmptyOrNull !== preview;

    const timezonedDateAsNumber = convertPacificDateTimeLocalStringToTimestamp(
      inputAsNonEmptyOrNull
    );
    return preview !== timezonedDateAsNumber;
  }, [inputValue, preview]);

  return (
    <>
      <div>
        Current preview (Pacific Time):
        <div
          className={classes(preview === null ? S.noPreview : S.currentPreview)}
        >
          {preview === null
            ? ' None'
            : previewDateFormatter.value.format(preview)}
        </div>
      </div>
      <PastPreviewAlert preview={preview} />
      <label htmlFor={inputId} className={S.previewLabel}>
        Change:
      </label>
      <input
        className={S.previewInput}
        id={inputId}
        name={inputId}
        type="datetime-local"
        defaultValue={inputDefaultValue}
        onInput={(e) => {
          setInputValue(e.currentTarget.value);
        }}
      />
      <div className={S.buttonContainer}>
        <Button
          className={S.button}
          size="sm"
          variant="secondary"
          disabled={isStartPending || preview === null}
          loading={isStopPending}
          onClick={() => {
            startStopTransition(() => {
              setPreview(EnvironmentService.url, null);
            });
          }}
        >
          Stop
        </Button>
        <Button
          id={startBtnId}
          className={S.button}
          size="sm"
          variant="primary"
          disabled={isStopPending || !isInputDifferentFromCurrentPreview}
          loading={isStartPending}
          onClick={() => {
            startStartTransition(() => {
              if (isNullOrEmpty(inputValue)) {
                setPreview(EnvironmentService.url, null);
                return;
              }

              const timezonedDateAsNumber =
                convertPacificDateTimeLocalStringToTimestamp(inputValue);
              setPreview(EnvironmentService.url, timezonedDateAsNumber);
            });
          }}
        >
          Start
        </Button>
      </div>
      <Button
        className={S.button}
        size="sm"
        variant="secondary"
        disabled={preview === null}
        onClick={() => {
          if (preview === null) return;
          window.navigator.clipboard.writeText(
            PreviewService.getPreviewURL(EnvironmentService.url, preview)
          );
        }}
      >
        Copy Current Preview Link
      </Button>
    </>
  );
});

/**
 * A component that allows setting a preview date. Is not displayed inproduction.
 */
const InternalPreview: FC = () => {
  const [show, setShow] = useLocalStorage<boolean>(
    'DECKERS_INTERNAL_SHOW_PREVIEW_MENU',
    false
  );

  return (
    <div className={S.previewContainer}>
      <Button
        className={S.previewButton}
        variant="text"
        onClick={() => setShow(true)}
      >
        <Icon icon={IconTypes.Eye} />
      </Button>
      <div className={classes(S.previewCard, show && S.visible)}>
        <div className={S.titleContainer}>
          <div className={S.title}>Preview</div>
          <Button
            variant="text"
            className={S.close}
            onClick={() => setShow(false)}
          >
            <Icon icon={IconTypes.CloseLight} />
          </Button>
        </div>
        <div className={S.menuContainer}>
          <CommonErrorBoundary fallback={null}>
            <Suspense>{show && <PreviewMenu />}</Suspense>
          </CommonErrorBoundary>
        </div>
      </div>
    </div>
  );
};

/**
 * A component that allows setting a preview date. Is not displayed inproduction.
 * @returns The preview component.
 */
export const Preview: FC = (process.env.NEXT_PUBLIC_APP_ENV === "prod")
  ? () => null
  : InternalPreview;
